import { useState, useContext } from "react";
import { Formik, Form as FormikForm, Field, ErrorMessage } from "formik";
import { Form, Row, Col } from "react-bootstrap";
import * as Yup from "yup";
import { UserDataContext } from "../components/UserDataContext";
import { registerLocale } from "react-datepicker";
import { addDays, isAfter } from "date-fns";
import { format } from "date-fns";
import "../components/button.css";
import { TextField } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { pl } from "date-fns/locale";
import { marginBottom } from "../App";
import Alerts from "../components/Alerts";
import Button from "@mui/material/Button";
import { ThemeProvider } from "@mui/material/styles";
import { theme } from "../components/theme";
import { apiRequest } from "../components/Config";
import { Box, Select } from "@mui/material";
import MenuItem from "@mui/material/MenuItem";

const currentYear = new Date().getFullYear();

const validationSchema = Yup.object().shape({
  birthYear: Yup.number()
    .typeError("Pole musi być liczbą") // jeśli ktoś wpisze np. tekst
    .required("Rok urodzenia jest wymagany")
    .min(1940, "Rok musi być co najmniej 1925")
    .max(currentYear - 20, `Rok nie może być większy niż ${currentYear - 20}`),
  fullName: Yup.string().required("Imię i nazwisko są wymagane"),
  mk: Yup.string()
    .nullable()
    .required("Płeć jest wymagana")
    .oneOf(["K", "M"], "Wybierz poprawną płeć"),
  circle: Yup.string()
    .nullable()
    .required("Wybierz koło...")
    .oneOf(
      ["KOŁO PRZY ODDZIALE PZITB", "KOŁO MŁODYCH", "KOŁO PSB BYTOM"],
      "Wybierz poprawne koło"
    ),
  pezs: Yup.string()
    .nullable()
    .required("Wybierz ...")
    .oneOf(["pracownik", "emeryt", "zwolniona 80", "zasłużony"], "Wybierz ..."),
  email: Yup.string()
    .email("Nieprawidłowy adres email")
    .required("Email jest wymagany"),
  selectedDate: Yup.date().nullable().required("Data jest wymagana"),
});

export default function AddContributor({ perm, refreshData }) {
  registerLocale("pl", pl);

  const { userData } = useContext(UserDataContext);
  const [apiResponse, setApiResponse] = useState({});
  const [showApiResponse, setShowApiResponse] = useState(false);

  const initialValues = {
    fullName: "",
    mk: "",
    email: "",
    circle: "",
    birthYear: "",
    phone: "",
    selectedDate: new Date(new Date().toDateString()),
    pezs: "",
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    const formatted = format(values.selectedDate, "yyyy-MM-dd");
    values["dateStart"] = formatted;
    //  console.log("Values: ", values);

    try {
      const response = await apiRequest("registerpzitb", "POST", values);

      if (response.error_msg === 200) {
        setShowApiResponse(true);
        setApiResponse({
          title: "Sukces",
          severity: "success",
          info: "Użytkownik dodany!",
        });
        refreshData();
      }
      if (response.error_msg === 500) {
        setApiResponse({
          title: "Błąd",
          severity: "error",
          info: "Coś poszło nie tak podczas rejestracji. Prawdopodobnie nie zostały wybrane uprawnienia użytkownika. Spróbuj ponownie lub skontaktuj się z autorem.",
        });
        setShowApiResponse(true);
      }
      if (response.error_msg === 501) {
        setApiResponse({
          title: "Błąd",
          severity: "error",
          info: "Taki email już istnieje w systemie!",
        });
        setShowApiResponse(true);
      }
    } catch (error) {
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      {showApiResponse && (
        <Row style={{ marginBottom }}>
          <Alerts data={apiResponse} />
        </Row>
      )}
      <h4>Rejestracja osoby PZITB - Katowice</h4>
      <ThemeProvider theme={theme}>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting, setFieldValue, values, handleChange }) => (
            <FormikForm autoComplete="off">
              <input
                type="text"
                name="nope"
                style={{ display: "none" }}
                autoComplete="off"
              />
              <Row style={{ marginBottom }} className="align-items-center">
                <Col md="3" style={{ marginBottom }}>
                  <Form.Group controlId="formFullName">
                    <TextField
                      label="Nazwisko i Imię"
                      name="fullName"
                      value={values.fullName || ""}
                      onChange={handleChange}
                      fullWidth
                      autoComplete="off"
                    />
                    <ErrorMessage
                      name="fullName"
                      component="div"
                      className="text-danger"
                    />
                  </Form.Group>
                </Col>

                <Col md="3" style={{ marginBottom }}>
                  <Box sx={{ minWidth: 120 }}>
                    <Select
                      name="mk"
                      value={values.mk || ""}
                      onChange={(e) => setFieldValue("mk", e.target.value)}
                      displayEmpty
                      fullWidth
                    >
                      <MenuItem value="">
                        <em>Płeć</em>
                      </MenuItem>
                      <MenuItem value="K">Kobieta</MenuItem>
                      <MenuItem value="M">Mężczyzna</MenuItem>
                    </Select>
                    <ErrorMessage
                      name="mk"
                      component="div"
                      className="text-danger"
                    />
                  </Box>
                </Col>
                <Col md="3" style={{ marginBottom }}>
                  <Form.Group controlId="formEmail">
                    <Field
                      as={TextField}
                      label="Email"
                      type="email"
                      name="email"
                      value={values.email || ""}
                      fullWidth
                    />
                    <ErrorMessage
                      name="email"
                      component="div"
                      className="text-danger"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md="3" style={{ marginBottom }}>
                  <Box sx={{ minWidth: 120 }}>
                    <Form.Group controlId="formCircle">
                      <Select
                        name="circle"
                        value={values.circle || ""}
                        onChange={(e) =>
                          setFieldValue("circle", e.target.value)
                        }
                        displayEmpty
                        fullWidth
                      >
                        <MenuItem value="">
                          <em>Koło</em>
                        </MenuItem>
                        <MenuItem value="KOŁO PRZY ODDZIALE PZITB">
                          KOŁO PRZY ODDZIALE PZITB
                        </MenuItem>
                        <MenuItem value="KOŁO MŁODYCH">KOŁO MŁODYCH</MenuItem>
                        <MenuItem value="KOŁO PSB BYTOM">
                          KOŁO PSB BYTOM
                        </MenuItem>
                      </Select>
                      <ErrorMessage
                        name="circle"
                        component="div"
                        className="text-danger"
                      />
                    </Form.Group>
                  </Box>
                </Col>
                <Col md="3" style={{ marginBottom }}>
                  <Form.Group controlId="formBirthYear">
                    <Field
                      as={TextField}
                      label="Rok urodzenia"
                      type="number"
                      name="birthYear"
                      value={values.birthYear || ""}
                      autoComplete="off"
                      fullWidth
                    />
                    <ErrorMessage
                      name="birthYear"
                      component="div"
                      className="text-danger"
                    />
                  </Form.Group>
                </Col>
                <Col md="3" style={{ marginBottom }}>
                  <Box sx={{ minWidth: 120 }}>
                    <Form.Group controlId="formPezs">
                      <Select
                        name="pezs"
                        value={values.pezs || ""}
                        onChange={(e) => setFieldValue("pezs", e.target.value)}
                        displayEmpty
                        fullWidth
                      >
                        <MenuItem value="">
                          <em>--</em>
                        </MenuItem>
                        <MenuItem value="pracownik">pracownik</MenuItem>
                        <MenuItem value="emeryt">emeryt</MenuItem>
                        <MenuItem value="zwolniona 80">zwolniona 80</MenuItem>
                        <MenuItem value="zasłużony">zasłużony</MenuItem>
                      </Select>
                      <ErrorMessage
                        name="pezs"
                        component="div"
                        className="text-danger"
                      />
                    </Form.Group>
                  </Box>
                </Col>{" "}
                <Col md="3" style={{ marginBottom }}>
                  <Form.Group controlId="formDate">
                    <LocalizationProvider
                      dateAdapter={AdapterDateFns}
                      adapterLocale={pl}
                    >
                      <DatePicker
                        label="Data przyjęcia"
                        value={values.selectedDate || ""}
                        selected={values.selectedDate}
                        onChange={(date) => setFieldValue("selectedDate", date)}
                        minDate={addDays(new Date(), 0)}
                        dateFormat="yyyy-MM-dd"
                        format="yyyy-MM-dd"
                        slotProps={{ textField: { variant: "outlined" } }}
                      />
                    </LocalizationProvider>
                    <ErrorMessage
                      name="selectedDate"
                      component="div"
                      className="text-danger"
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col>
                  <Button
                    variant="contained"
                    type="submit"
                    disabled={isSubmitting}
                  >
                    Rejestruj
                  </Button>
                </Col>
              </Row>

              <hr></hr>
            </FormikForm>
          )}
        </Formik>
      </ThemeProvider>
    </div>
  );
}
